import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  LiveKitRoom,
  LayoutContextProvider,
  GridLayout,
  ParticipantTile,
  RoomAudioRenderer,
  ControlBar,
  useTracks,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import "@livekit/components-styles";
import { useLiveSession } from "../../hooks/useLiveSession";
import { useLiveComments } from "../../hooks/useLiveComments";
import { getLiveCommentAuthorName } from "../../utils/liveCommentDisplay";
import { requestJoinLive, fetchMyJoinRequests } from "../../redux/thunks/medicalCasesThunks";
import "./LiveNowViewerRoom.css";

function WatcherStage({ canPublish }) {
  const allTracks = useTracks([Track.Source.Camera, Track.Source.ScreenShare], {
    onlySubscribed: false,
  });
  // A pure watcher has nothing to show on their own tile — hide it so the grid
  // isn't a confusing paginated carousel starting on an empty local placeholder.
  const tracks = canPublish ? allTracks : allTracks.filter((t) => !t.participant.isLocal);

  return (
    <div className="mcd-live-now__livekit-stage">
      <LayoutContextProvider>
        <GridLayout tracks={tracks}>
          <ParticipantTile />
        </GridLayout>
        <RoomAudioRenderer />
        {canPublish ? (
          <ControlBar
            controls={{ camera: true, microphone: true, screenShare: false, chat: false, leave: false }}
          />
        ) : null}
      </LayoutContextProvider>
    </div>
  );
}

function connectionLabel(status) {
  const labels = {
    idle: "Not connected",
    joining: "Requesting access…",
    ready: "Connecting to media…",
    "sdk-connected": "Media connected — awaiting server confirmation…",
    "backend-connected": "Connected",
    leaving: "Leaving…",
    disconnected: "Disconnected",
    failed: "Connection failed",
  };
  return labels[status] || labels.idle;
}

export default function LiveNowViewerRoom({ streamData, doctor }) {
  const streamId = streamData?.id;
  const dispatch = useDispatch();
  const [credentials, setCredentials] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [joinRequestStatus, setJoinRequestStatus] = useState("idle"); // idle | pending | error
  const {
    connection,
    connectedParticipants,
    currentParticipant,
    joinOnce,
    leaveOnce,
    markSdkConnected,
    markSdkError,
  } = useLiveSession(streamId, doctor);
  const backendConnected = connection?.status === "backend-connected";
  const { comments, error: commentsError, sendComment } = useLiveComments(streamId, backendConnected);
  const canPublish = Boolean(
    currentParticipant?.canPublishAudio || currentParticipant?.canPublishVideo
  );

  const handleRequestJoin = async () => {
    setJoinRequestStatus("pending");
    try {
      await dispatch(requestJoinLive(streamId)).unwrap();
    } catch (error) {
      const message = typeof error === "string" ? error : error?.message || "";
      // Already-pending is not a failure from the viewer's point of view.
      if (!message.includes("ALREADY_PENDING")) {
        setJoinRequestStatus("error");
        setActionError(message || "Unable to request to join");
        return;
      }
    }
    setJoinRequestStatus("pending");
  };

  useEffect(() => {
    if (joinRequestStatus !== "pending" || !streamId) return undefined;

    let cancelled = false;
    const refresh = () => {
      dispatch(fetchMyJoinRequests())
        .unwrap()
        .then((result) => {
          if (cancelled) return;
          const items = result?.items || [];
          const mine = items.find((item) => item.liveId === streamId);
          if (mine && ["rejected", "cancelled", "expired"].includes(mine.status)) {
            setJoinRequestStatus("idle");
          }
        })
        .catch(() => {});
    };
    const timer = window.setInterval(refresh, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [dispatch, joinRequestStatus, streamId]);

  useEffect(() => {
    if (!streamId || credentials || actionLoading) return undefined;

    let cancelled = false;
    setActionLoading(true);
    setActionError(null);
    joinOnce()
      .then((result) => {
        if (!cancelled) {
          setCredentials({ serverUrl: result.serverUrl, participantToken: result.participantToken });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setActionError(typeof error === "string" ? error : error?.message || "Unable to watch the live");
        }
      })
      .finally(() => {
        if (!cancelled) setActionLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamId]);

  const handleLeave = async () => {
    setActionLoading(true);
    await leaveOnce();
    setCredentials(null);
    setActionLoading(false);
  };

  const handleSend = async (event) => {
    event.preventDefault();
    const content = chatInput.trim();
    if (!content || !backendConnected) return;
    try {
      await sendComment(content);
      setChatInput("");
    } catch (error) {
      setActionError(typeof error === "string" ? error : error?.message || "Unable to send the comment");
    }
  };

  return (
    <section className="mcd-live-now" aria-label={streamData?.title || "Live Stream"}>
      <div className="mcd-live-now__layout">
        <article className="mcd-live-now__left">
          <div className="mcd-live-now__video">
            <div className="mcd-live-now__topbar">
              <span className="mcd-live-now__live-pill">LIVE</span>
              <span className="mcd-live-now__watching-pill">{connectedParticipants.length} connected</span>
            </div>

            {credentials ? (
              <LiveKitRoom
                serverUrl={credentials.serverUrl}
                token={credentials.participantToken}
                connect
                audio={canPublish}
                video={canPublish}
                onConnected={markSdkConnected}
                onDisconnected={() => { setCredentials(null); void leaveOnce(); }}
                onError={(error) => {
                  markSdkError();
                  setActionError(error?.message || "Unable to connect to LiveKit");
                }}
              >
                <WatcherStage canPublish={canPublish} />
              </LiveKitRoom>
            ) : (
              <div className="mcd-live-now__case-box">
                <h2>{streamData?.title || "Live medical discussion"}</h2>
                <p>
                  {actionError
                    ? actionError
                    : streamData?.description || "Connecting to the live…"}
                </p>
              </div>
            )}
          </div>

          <div className="mcd-live-now__status-bar" aria-live="polite">
            <span>{connectionLabel(connection?.status)}</span>
            {credentials && !canPublish ? (
              <button
                type="button"
                onClick={handleRequestJoin}
                disabled={joinRequestStatus === "pending"}
              >
                {joinRequestStatus === "pending" ? "Request pending…" : "Ask to join"}
              </button>
            ) : null}
            {credentials ? (
              <button type="button" onClick={handleLeave} disabled={actionLoading}>Leave Stream</button>
            ) : null}
          </div>
          {actionError ? <p role="alert">{actionError}</p> : null}
        </article>

        <aside className="mcd-live-now__chat-panel">
          <header className="mcd-live-now__chat-header">
            <h4>Live Chat ({connectedParticipants.length} participants)</h4>
            <p>{backendConnected ? "Join the discussion" : "Chat unlocks after server confirmation"}</p>
          </header>
          <div className="mcd-live-now__chat-body">
            {comments.map((comment) => (
              <div key={comment.id} className="mcd-live-now__message">
                <div className="mcd-live-now__msg-content">
                  <strong>{getLiveCommentAuthorName(comment, doctor)}</strong>
                  <p>{comment.content}</p>
                </div>
              </div>
            ))}
            {commentsError ? <p role="alert">{commentsError}</p> : null}
          </div>
          <form className="mcd-live-now__chat-form" onSubmit={handleSend}>
            <div className="mcd-live-now__chat-input-wrap">
              <input type="text" value={chatInput} onChange={(event) => setChatInput(event.target.value)}
                placeholder={backendConnected ? "Ask a question…" : "Waiting for connection confirmation"}
                disabled={!backendConnected} />
              <button type="submit" disabled={!backendConnected || !chatInput.trim()}>Send</button>
            </div>
          </form>
        </aside>
      </div>
    </section>
  );
}
